import axios from 'axios'
import { basicAuthConfig } from '../configs'
import { urlMyspreadApi } from '../urls'

const BASE = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/email-templates'

/**
 * Obtener todos los templates de email activos
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Lista de templates
 */
export const getEmailTemplates = async (user) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.get(`${BASE}/`, config)
        return {
            success: true,
            data: response.data.data || []
        }
    } catch (error) {
        console.error('Error al obtener templates de email:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al obtener templates de email',
            data: []
        }
    }
}

/**
 * Obtener un template por ID (sin HTML)
 * @param {Object} user - Usuario autenticado
 * @param {number} templateId - ID del template
 * @returns {Promise} Detalle del template
 */
export const getEmailTemplateById = async (user, templateId) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.get(`${BASE}/${templateId}`, config)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error) {
        console.error('Error al obtener template de email:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al obtener template de email',
            data: null
        }
    }
}

/**
 * Obtener un template por ID incluyendo contenido HTML
 * @param {Object} user - Usuario autenticado
 * @param {number} templateId - ID del template
 * @returns {Promise} Detalle del template con HTML
 */
export const getEmailTemplateContent = async (user, templateId) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.get(`${BASE}/${templateId}/content`, config)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error) {
        console.error('Error al obtener contenido del template:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al obtener contenido del template',
            data: null
        }
    }
}

/**
 * Crear un nuevo template de email
 * @param {Object} user - Usuario autenticado
 * @param {Object} templateData - Datos del template
 * @returns {Promise} Template creado
 */
export const createEmailTemplate = async (user, templateData) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.post(`${BASE}/`, templateData, config)
        return {
            success: true,
            data: response.data.data,
            message: response.data.message
        }
    } catch (error) {
        console.error('Error al crear template de email:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al crear template de email',
            data: null
        }
    }
}

/**
 * Actualizar metadatos de un template
 * @param {Object} user - Usuario autenticado
 * @param {number} templateId - ID del template
 * @param {Object} templateData - Datos a actualizar
 * @returns {Promise} Template actualizado
 */
export const updateEmailTemplate = async (user, templateId, templateData) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.put(`${BASE}/${templateId}`, templateData, config)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error) {
        console.error('Error al actualizar template de email:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al actualizar template de email',
            data: null
        }
    }
}

/**
 * Actualizar el contenido HTML de un template en Blob Storage
 * @param {Object} user - Usuario autenticado
 * @param {number} templateId - ID del template
 * @param {string} htmlContent - Contenido HTML del template
 * @returns {Promise} Resultado de la operación
 */
export const updateEmailTemplateContent = async (user, templateId, htmlContent) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.put(
            `${BASE}/${templateId}/content`,
            { html_content: htmlContent },
            config
        )
        return {
            success: true,
            data: response.data.data
        }
    } catch (error) {
        console.error('Error al actualizar contenido del template:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al actualizar contenido del template',
            data: null
        }
    }
}

/**
 * Eliminar (soft-delete) un template de email
 * @param {Object} user - Usuario autenticado
 * @param {number} templateId - ID del template
 * @returns {Promise} Resultado de la operación
 */
export const deleteEmailTemplate = async (user, templateId) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.delete(`${BASE}/${templateId}`, config)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error) {
        console.error('Error al eliminar template de email:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al eliminar template de email',
            data: null
        }
    }
}

/**
 * Enviar correo de prueba usando un template
 * @param {Object} user - Usuario autenticado
 * @param {number} templateId - ID del template
 * @param {string} correoDestino - Email de destino para la prueba
 * @param {Object} [valoresPrueba] - Valores de reemplazo para la prueba
 * @returns {Promise} Resultado del envío
 */
export const testEmailTemplate = async (user, templateId, correoDestino, valoresPrueba = {}) => {
    try {
        const config = basicAuthConfig(user)
        const response = await axios.post(
            `${BASE}/${templateId}/test`,
            { correo_destino: correoDestino, valores_prueba: valoresPrueba },
            config
        )
        return {
            success: true,
            data: response.data.data
        }
    } catch (error) {
        console.error('Error al enviar correo de prueba:', error)
        return {
            success: false,
            error: error.response?.data?.error || 'Error al enviar correo de prueba',
            data: null
        }
    }
}
